import { useNotifications } from '@sequencehq/core-components'
import { useCreditNoteQuery } from './useCreditNote'
import { extractValidationErrors, useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

export const useCreditNoteActions = (creditNoteId: string) => {
  const { displayNotification } = useNotifications()
  const { data: creditNote } = useCreditNoteQuery(creditNoteId)

  const saveCreditNoteMutation = useMutation(
    dashboardv99990101Client.putCreditNote
  )

  const issueCreditNoteMutation = useMutation(
    dashboardv99990101Client.postFinalizeAndSendCreditNote,
    {
      onSuccess: data => {
        displayNotification(
          `Credit note ${data?.creditNoteNumber || ''} sent`,
          {
            type: 'success'
          }
        )
      },
      onError: error => {
        const [firstValidationError] = extractValidationErrors(error)
        displayNotification('Failed to issue credit note', {
          type: 'error',
          description: firstValidationError
        })
      }
    }
  )
  const voidCreditNoteMutation = useMutation(
    dashboardv99990101Client.postVoidCreditNote,
    {
      onSuccess: () => {
        displayNotification('Credit note voided', {
          type: 'success'
        })
      },
      onError: () => {
        displayNotification('Failed to void credit note', {
          type: 'error'
        })
      }
    }
  )
  const sendTestCreditNoteMutation = useMutation(
    dashboardv99990101Client.postSendTestCreditNote,
    {
      onSuccess: () => {
        displayNotification('Test credit note sent', {
          type: 'success'
        })
      },
      onError: () => {
        displayNotification('Failed to send test credit note', {
          type: 'error'
        })
      }
    }
  )

  const markAsSentCreditNoteMutation = useMutation(
    dashboardv99990101Client.postMarkAsSentCreditNote,
    {
      onSuccess: () => {
        displayNotification('Credit note marked as sent', {
          type: 'success'
        })
      },
      onError: error => {
        const [firstValidationError] = extractValidationErrors(error)
        displayNotification('Failed to mark credit note as sent', {
          type: 'error',
          description: firstValidationError
        })
      }
    }
  )

  const issueCreditNote = {
    mutateAsync: () =>
      issueCreditNoteMutation.mutateAsync({
        id: creditNoteId
      }),
    isLoading: issueCreditNoteMutation.isPending
  }

  const voidCreditNote = {
    mutate: () => {
      voidCreditNoteMutation.mutate({
        id: creditNoteId
      })
    },
    isLoading: voidCreditNoteMutation.isPending
  }

  const sendTestCreditNote = {
    mutateAsync: (testEmail: string) =>
      sendTestCreditNoteMutation.mutateAsync({
        id: creditNoteId,
        testEmail
      }),
    isLoading: sendTestCreditNoteMutation.isPending
  }

  const grantCreditAction = {
    mutate: (shouldGrantCredit: boolean) => {
      saveCreditNoteMutation.mutate({
        id: creditNoteId,
        body: {
          ...creditNote,
          settings: {
            generateCashCreditGrant: shouldGrantCredit ? 'ENABLED' : 'DISABLED'
          }
        }
      })
    },
    isLoading: saveCreditNoteMutation.isPending
  }

  const markAsSent = {
    mutateAsync: () =>
      markAsSentCreditNoteMutation.mutateAsync({
        id: creditNoteId
      }),
    isLoading: markAsSentCreditNoteMutation.isPending
  }

  return {
    issueCreditNote,
    sendTestCreditNote,
    voidCreditNote,
    grantCreditAction,
    markAsSent
  }
}
